/**
 * New Developments — What-If Simulator
 *
 * Sandbox: takes the V6 baseline natural decline and layers a single proposed
 * use-case demand on top → runway + a yield panel (production, revenue, $/AF).
 *
 * This is intentionally SEPARATE from the main dashboard: choosing a use case
 * here does NOT change the empirical decline shown on the Hydrograph/Volume
 * tabs. It answers "if I developed THIS here, how long would the water last
 * and what would it produce?"
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';
import { ArrowPathIcon, BeakerIcon, BoltIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/client';

interface Props {
  siteId: string;
  volumeAF: number;
  currentSatFt: number;
  declineRate: number;   // V6 baseline natural decline (ft/yr)
  wellDepth: number;
  siteAcres: number;
  aquifer: string;
  initialUseCase?: string;
}

interface UseCaseOpt {
  key: string; name: string; label: string; unit: string;
  af_per_unit: number; group: string; land_density_per_acre?: number | null;
}

// Compact yield formatting (e.g. 146.0M, 512K, 8,000).
function fmtYield(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return n.toLocaleString();
}

const _GAL_PER_AF = 325851;
const _MIN_PER_YR = 525960;

// Starting capacity: land-based uses (crops/livestock) = acreage x stocking rate;
// water-based uses (data center, municipal, etc.) get a sensible default by unit.
function defaultScaleFor(uc: UseCaseOpt | undefined, siteAcres: number): number {
  if (!uc) return 0;
  if (uc.land_density_per_acre != null) {
    return Math.max(1, Math.round(siteAcres * uc.land_density_per_acre));
  }
  const u = uc.unit.toLowerCase();
  if (u.includes('mw')) return 5;
  if (u.includes('population') || u.includes('person') || u.includes('people')) return 10000;
  if (u.includes('well')) return 20;
  if (u.includes('mgy')) return 50;
  return 1;
}

export function NewDevelopmentPanel({ siteId, volumeAF, currentSatFt, declineRate, wellDepth, siteAcres, aquifer, initialUseCase }: Props) {
  const [useCases, setUseCases] = useState<UseCaseOpt[]>([]);
  const [useCase, setUseCase] = useState(initialUseCase || 'corn');
  const [scale, setScale] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  const ucInfo = useCases.find(u => u.key === useCase);

  // Load the use-case catalog from the engine (single source of truth)
  useEffect(() => {
    apiClient.get('/phreatic/calculator/demand-use-cases')
      .then(resp => {
        const flat: UseCaseOpt[] = [];
        const groups = resp.data?.groups || {};
        for (const [group, items] of Object.entries<any[]>(groups)) {
          for (const it of items) flat.push({ ...it, group });
        }
        setUseCases(flat);
        const start = flat.find(u => u.key === (initialUseCase || 'corn')) || flat[0];
        if (start) {
          setUseCase(start.key);
          setScale(defaultScaleFor(start, siteAcres));
        }
      })
      .catch(() => setError('Could not load use cases'));
  }, [initialUseCase, siteAcres]);

  const simulate = useCallback(async () => {
    if (!scale || scale <= 0) { setError('Enter a capacity greater than zero'); return; }
    setLoading(true);
    setError(null);
    try {
      const resp = await apiClient.post('/phreatic/calculator/new-development-simulate', {
        site_id: siteId,
        use_case: useCase,
        scale,
        volume_available_af: volumeAF,
        current_sat_thickness_ft: currentSatFt,
        projected_decline_ft_yr: declineRate,
        well_depth_ft: wellDepth,
        aquifer,
      });
      setResult(resp.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Simulation failed');
    } finally {
      setLoading(false);
    }
  }, [siteId, useCase, scale, volumeAF, currentSatFt, declineRate, wellDepth, aquifer]);

  const groupNames = Array.from(new Set(useCases.map(u => u.group)));

  // Live demand preview (before running) — mirrors the dashboard's parcel demand tile
  const previewAF = ucInfo ? Math.round(ucInfo.af_per_unit * scale * 10) / 10 : 0;
  const previewGpm = Math.round((previewAF * _GAL_PER_AF / _MIN_PER_YR) * 10) / 10;

  // Chart data — extend the curve to the depletion point so the graph shows the
  // FULL runway (the engine stops at the last positive-sat year; add a terminal
  // year at sat=0 so the line visibly reaches the end of the runway).
  const yby: any[] = result?.year_by_year || [];
  const chartData = yby.length > 0 && yby[yby.length - 1].sat_thickness_ft > 0
    ? [...yby, { year: yby[yby.length - 1].year + 1, sat_thickness_ft: 0 }]
    : yby;

  return (
    <div className="space-y-3">
      {/* Controls — carbon copy of the dashboard "Site Assessment" container */}
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-3 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold text-mono-400 uppercase tracking-wider">
            <BeakerIcon className="w-3.5 h-3.5 inline mr-1" />
            Proposed Development
          </h3>
          <div className="flex gap-2">
            <button onClick={simulate} disabled={loading}
              className="flex items-center gap-1 px-3 py-1 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 disabled:opacity-50">
              {loading ? <ArrowPathIcon className="w-3 h-3 animate-spin" /> : <BoltIcon className="w-3 h-3" />}
              {loading ? 'Running...' : 'Simulate'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] text-mono-500 uppercase tracking-wider">Use Case</label>
            <select value={useCase}
              onChange={e => {
                setUseCase(e.target.value);
                const uc = useCases.find(u => u.key === e.target.value);
                if (uc) setScale(defaultScaleFor(uc, siteAcres));
              }}
              className="w-full mt-0.5 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200 focus:border-cyan-500 focus:outline-none">
              <option value="" disabled>Select use case...</option>
              {groupNames.map(g => (
                <optgroup key={g} label={g}>
                  {useCases.filter(u => u.group === g).map(u => <option key={u.key} value={u.key}>{u.name}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-mono-500 uppercase tracking-wider">Capacity ({ucInfo?.unit || 'units'})</label>
            <input type="number" value={scale} onChange={e => setScale(Number(e.target.value))}
              className="w-full mt-0.5 px-2 py-1 text-xs border rounded focus:outline-none bg-mono-800 border-mono-700 text-mono-200 focus:border-cyan-500" />
          </div>
          <div className="bg-mono-800/50 rounded p-1.5">
            <div className="text-[9px] text-mono-500 uppercase">Proposed Demand</div>
            <div className="text-xs text-cyan-400 font-semibold">{previewAF.toLocaleString()} AF/yr</div>
            <div className="text-[9px] text-mono-400">{previewGpm.toLocaleString()} GPM avg</div>
          </div>
          <div className="bg-mono-800/50 rounded p-1.5">
            <div className="text-[9px] text-mono-500 uppercase">Water Available</div>
            <div className="text-xs text-cyan-400 font-semibold">{volumeAF.toLocaleString()} AF</div>
            <div className="text-[9px] text-mono-400">Sat: {currentSatFt.toFixed(0)} ft · decline {declineRate.toFixed(2)} ft/yr</div>
          </div>
        </div>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 text-red-400 text-xs">{error}</div>}

      {result && (
        <>
          {/* Runway + Yield summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="bg-mono-900 border border-cyan-500/30 rounded-lg p-2 text-center">
              <div className="text-[9px] text-mono-500 uppercase">{result.floyd_applicable === false ? 'Runway' : 'Runway (Floyd-adj.)'}</div>
              <div className="text-xl font-bold text-cyan-400">{result.years_remaining_adjusted}<span className="text-xs text-mono-500"> yr</span></div>
            </div>
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-2 text-center">
              <div className="text-[9px] text-mono-500 uppercase">Runway (simple)</div>
              <div className="text-xl font-bold text-mono-200">{result.years_remaining_simple}<span className="text-xs text-mono-500"> yr</span></div>
            </div>
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-2 text-center">
              <div className="text-[9px] text-mono-500 uppercase">Demand</div>
              <div className="text-lg font-bold text-mono-200">{result.annual_demand_af.toLocaleString()}<span className="text-xs text-mono-500"> AF/yr</span></div>
            </div>
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-2 text-center">
              <div className="text-[9px] text-mono-500 uppercase">Avg Pump</div>
              <div className="text-lg font-bold text-mono-200">{result.avg_gpm.toLocaleString()}<span className="text-xs text-mono-500"> GPM</span></div>
            </div>
            <div className="bg-mono-900 border border-cyan-500/30 rounded-lg p-2 text-center">
              <div className="text-[9px] text-mono-500 uppercase">Annual Yield</div>
              <div className="text-lg font-bold text-cyan-400">{fmtYield(result.yield?.annual_yield || 0)}<span className="text-xs text-mono-500"> {result.yield?.yield_unit}</span></div>
            </div>
            <div className="bg-mono-900 border border-cyan-500/30 rounded-lg p-2 text-center">
              <div className="text-[9px] text-mono-500 uppercase">Yield / AF</div>
              <div className="text-lg font-bold text-cyan-400">{fmtYield(result.yield?.yield_per_af || 0)}<span className="text-xs text-mono-500"> {result.yield?.yield_unit}/AF</span></div>
            </div>
            <div className="bg-mono-900 border border-cyan-500/30 rounded-lg p-2 text-center">
              <div className="text-[9px] text-mono-500 uppercase">Return on Water</div>
              <div className="text-lg font-bold text-cyan-400">${result.yield?.revenue_per_af?.toLocaleString()}<span className="text-xs text-mono-500">/AF</span></div>
            </div>
            <div className="bg-mono-900 border border-cyan-500/30 rounded-lg p-2 text-center">
              <div className="text-[9px] text-mono-500 uppercase">Annual Revenue</div>
              <div className="text-lg font-bold text-cyan-400">${(result.yield?.annual_revenue / 1e6).toFixed(2)}M</div>
            </div>
          </div>

          {/* Revenue basis — price + source (USDA live vs market estimate) */}
          {result.yield?.unit_price != null && (
            <div className="text-[10px] text-mono-500">
              Revenue = yield × ${result.yield.unit_price}/{result.yield.yield_unit}
              {' · '}
              {result.yield.price_source === 'usda_live'
                ? 'USDA live price'
                : result.yield.price_source === 'custom'
                ? 'custom price'
                : 'market estimate'}
            </div>
          )}

          {/* Year-by-year decline under load */}
          {result.year_by_year?.length > 0 && (
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
              <h3 className="text-xs font-semibold text-mono-300 uppercase tracking-wider mb-3">
                Saturated Thickness Under Load — {result.label}
              </h3>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="satGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="year" tick={{ fill: '#888', fontSize: 10 }}
                    label={{ value: 'Year', position: 'insideBottom', offset: -2, fill: '#666', fontSize: 9 }} />
                  <YAxis tick={{ fill: '#888', fontSize: 10 }} tickFormatter={(v: number) => `${v} ft`} />
                  <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', fontSize: 11, borderRadius: 6 }}
                    formatter={(v: number, name: string) => [
                      name === 'sat_thickness_ft' ? `${v.toLocaleString()} ft` : `${v.toLocaleString()} AF`,
                      name === 'sat_thickness_ft' ? 'Sat Thickness' : 'Remaining',
                    ]} />
                  {result.floyd_applicable !== false && (
                    <ReferenceLine y={40} stroke="#f59e0b" strokeDasharray="4 4"
                      label={{ value: 'Floyd 40ft', fill: '#f59e0b', fontSize: 9, position: 'insideTopRight' }} />
                  )}
                  <Area type="monotone" dataKey="sat_thickness_ft" stroke="#06b6d4" strokeWidth={2} fill="url(#satGrad)" />
                </AreaChart>
              </ResponsiveContainer>
              <div className="text-[10px] text-mono-500 mt-1">
                {result.floyd_applicable === false
                  ? `Floyd Factor N/A — ${result.aquifer_geology?.aquifer} is a ${result.aquifer_geology?.confinement} ${result.aquifer_geology?.lithology} aquifer (pressure/fracture-governed, not thickness-limited).`
                  : 'Floyd Factor: once saturated thickness drops below 40 ft, pump efficiency degrades and runway shortens.'}
              </div>
            </div>
          )}
        </>
      )}

      {!result && !loading && (
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 text-center text-mono-500 text-sm">
          Pick a proposed use and capacity, then Simulate to project runway + yield.
        </div>
      )}
    </div>
  );
}
